/**
 * EAA-1818: pure drop-legality rules for the hierarchy tree — kept isolated from
 * React/state so they stay easy to reason about and so the pointer path, the
 * keyboard path and the "Chuyển đến…" picker all grade a candidate identically.
 *
 * WHAT IS ACTUALLY CHECKED HERE, and how authoritative each check is:
 *
 *   1. Cycle / self-descendant — an đơn vị cannot move into itself or into its
 *      own subtree.                                          → danger, RELIABLE
 *      Mirrors `OrganizationService.validate_parent_change` rule 1. Reliable
 *      because the walk only ever needs nodes that are already on screen: to
 *      drop ONTO a target the user must have expanded down to it, so the whole
 *      path is loaded.
 *   2. Inactive target — the parent must be `is_active`.      → danger, RELIABLE
 *      Mirrors rule 3. `Organization.is_active` rides on every row we render.
 *   3. Cross-root — the target must sit in the SAME root tree as the org's
 *      current position, and only when the org already HAS a parent (a root org
 *      has no tree of its own to violate).                   → danger, BEST EFFORT
 *      Mirrors rule 2. This one cannot always be answered on the client: the
 *      tree is lazily loaded one level at a time, so an ancestor chain is only
 *      walkable when the intermediate orgs happen to be loaded. When it is not
 *      walkable, `checkCrossRoot` returns `null` (= "no objection") and the
 *      server stays authoritative — a PATCH that violates the rule comes back
 *      as VALIDATION_ERROR and is surfaced through
 *      `translateParentRuleMessage`. Deliberately NO extra fetches are issued
 *      just to pre-validate: a speculative round-trip on every drag-hover would
 *      cost more than letting the real write answer.
 *   4. Unit-type containment — the parent's type must LIST the child's type among
 *      its `allowed_children` ("Loại đơn vị không chứa được loại này").
 *                                                            → danger, BEST EFFORT
 *      EAA-1838. Mirrors `validate_parent_change`'s rule 4. This replaced an
 *      earlier rank comparison: `rank` was a hand-typed integer that could
 *      duplicate, drift from the real depth, and could not express a parent
 *      holding two different child types. Containment is graded off explicit
 *      edges instead. Best effort for the SAME reason as cross-root, and to the
 *      same honesty standard: `checkUnitTypeContainment` returns `null` ("no
 *      objection") whenever either side's type is unset or its `OrgUnitType` row
 *      is not loaded, and NO extra request is fired to pre-validate a hover. The
 *      vocabulary is fetched once per root org for the badges anyway
 *      (`useOrgUnitTypes`), so in practice the data is present — but a rule that
 *      guesses when it is not would reject moves the server would have allowed.
 *   5. Level → đơn vị — a Level is never a tree node.        → neutral, RELIABLE
 *      Not a backend rule at all; it is a client-side statement about what the
 *      two lists mean (see `types.ts`). A Level has no parent to set.
 */

import type { Organization, OrgUnitType } from '@/api/orgAdmin';
import type {
    DragSource,
    DragTarget,
    IllegalDropInfo,
    MoveBlockReason,
    OrganizationTreeNode,
} from './types';

/** Depth-first search for `candidateId` under `root` (inclusive of `root` itself). */
function containsId(root: OrganizationTreeNode, candidateId: string): boolean {
    if (root.id === candidateId) return true;
    return root.children.some((child) => containsId(child, candidateId));
}

export function findNode(nodes: OrganizationTreeNode[], id: string): OrganizationTreeNode | null {
    for (const node of nodes) {
        if (node.id === id) return node;
        const found = findNode(node.children, id);
        if (found) return found;
    }
    return null;
}

export interface CheckDropArgs {
    source: DragSource;
    target: DragTarget;
}

/**
 * Structural rules that need nothing but the source/target identity — the cheap
 * pass run on every pointer move. Returns the reason a drop is illegal, or
 * `null` when nothing here objects. "Legal" from this function means only "none
 * of these rules is broken"; `checkSelfDescendant`, `checkInactiveTarget` and
 * `checkCrossRoot` add the rules that need tree shape, and the server adds the
 * final word.
 */
export function checkDrop({ source, target }: CheckDropArgs): IllegalDropInfo | null {
    // Rule 5: Level -> đơn vị is always illegal, in either drop mode.
    if (source.kind === 'level') {
        if (target.kind === 'org') {
            return {
                reason: 'Level không kéo vào đơn vị — Level thuộc tổ chức, không phải một cấp trong cây',
                tone: 'neutral',
            };
        }
        // Level dragged onto another Level: reordering within the org-wide list.
        return null;
    }

    // From here `source.kind === 'org'`. An đơn vị can only ever be dropped
    // relative to another đơn vị row: the Level group renders as its own list
    // and never registers itself as a drop target for a dragged đơn vị.
    if (target.kind === 'level') {
        return null;
    }

    if (target.id === source.id) {
        return { reason: 'Không thể chuyển một đơn vị vào chính nó', tone: 'danger' };
    }

    return null;
}

/** Rule 1 — self-descendant, evaluated once the tree shape is known. */
export function checkSelfDescendant(
    roots: OrganizationTreeNode[],
    sourceId: string,
    targetId: string
): IllegalDropInfo | null {
    const sourceNode = findNode(roots, sourceId);
    if (!sourceNode) return null;
    if (containsId(sourceNode, targetId)) {
        return {
            reason: 'Không thể chuyển một đơn vị vào chính nhánh con của nó',
            tone: 'danger',
        };
    }
    return null;
}

/** Rule 3 — the candidate parent must still be active. */
export function checkInactiveTarget(target: Organization | null): IllegalDropInfo | null {
    if (target && !target.is_active) {
        return {
            reason: 'Không thể chuyển vào đơn vị đang ngưng hoạt động — bật lại đơn vị đích trước',
            tone: 'danger',
        };
    }
    return null;
}

/**
 * Walk `.parent` up to the root of the org's current tree, using only orgs
 * present in `known`. Returns `null` the moment the chain leaves what is
 * loaded — the caller must then treat the root as unknown rather than guess.
 * `seen` guards against a malformed cycle in the data so this cannot spin.
 */
export function resolveRootId(orgId: string, known: Map<string, Organization>): string | null {
    let cursor = known.get(orgId);
    if (!cursor) return null;
    const seen = new Set<string>([cursor.id]);
    while (cursor.parent !== null) {
        const next = known.get(cursor.parent);
        if (!next || seen.has(next.id)) return null;
        seen.add(next.id);
        cursor = next;
    }
    return cursor.id;
}

/**
 * Rule 2 — cross-root, BEST EFFORT (see the module doc). `null` means "no
 * objection", which covers both "same root" and "cannot tell from here".
 *
 * The `source.parent === null` short-circuit is not an optimization, it mirrors
 * the server: `validate_parent_change` skips the root comparison entirely when
 * the org has no parent yet, so a root org may legally be filed under any
 * active target.
 */
export function checkCrossRoot(
    source: Organization,
    targetId: string,
    known: Map<string, Organization>
): IllegalDropInfo | null {
    if (source.parent === null) return null;
    const sourceRoot = resolveRootId(source.id, known);
    const targetRoot = resolveRootId(targetId, known);
    if (!sourceRoot || !targetRoot || sourceRoot === targetRoot) return null;
    return {
        // EAA-1891: tagged so `completeDrop` can route a cross-tree drop into the
        // adoption dialog rather than rejecting it. The rule itself is unchanged
        // and still an objection: the drag path writes `PATCH parent`, which the
        // server still refuses across trees, and it performs no unit-type
        // vocabulary migration. Answering "you can, but not this way" is what the
        // tag buys.
        rule: 'cross-root',
        reason: 'Không thể chuyển đơn vị sang cây tổ chức khác bằng cách kéo–thả — dùng “Chuyển vào tổ chức khác”',
        tone: 'danger',
    };
}

/**
 * EAA-1838: Rule 4 — unit-type containment, BEST EFFORT (see the module doc).
 *
 * Reproduces `OrganizationService.validate_parent_change`'s fourth rule exactly,
 * including its escape hatch, because a client rule that is merely *similar* to
 * the server's is worse than none — it would block legal moves:
 *
 *   - Either side without a type → skip, matching the server's
 *     `org.unit_type_id is not None and new_parent.unit_type_id is not None`.
 *     This is a defensive path on both sides now that `create` always resolves a
 *     type, not the common case it was when the FK was `tier`.
 *   - Otherwise the parent's type must LIST the child's type id in
 *     `allowed_children`. Self-nesting needs no special case here: a type that
 *     may nest under itself contains its own id, so the same lookup answers it.
 *
 * `unitTypesById` not containing a referenced id means the vocabulary has not
 * loaded (or the org belongs to another root's) — that is "cannot tell", not
 * "illegal", so it returns `null` and the PATCH stays the authority.
 */
export function checkUnitTypeContainment(
    source: Organization,
    target: Organization | null,
    unitTypesById: Map<string, OrgUnitType>
): IllegalDropInfo | null {
    if (!target || !source.unit_type || !target.unit_type) return null;
    const childType = unitTypesById.get(source.unit_type);
    const parentType = unitTypesById.get(target.unit_type);
    if (!childType || !parentType) return null;
    if (parentType.allowed_children.includes(childType.id)) return null;
    return {
        reason: `Loại đơn vị không hợp — "${parentType.name}" không chứa được "${childType.name}"`,
        tone: 'danger',
    };
}

/**
 * Grade one candidate parent for the "Chuyển đến…" picker (frame 31). Shares the
 * rules above so a row greyed out in the dialog is exactly a row the tree would
 * refuse — with the same best-effort caveat on `cross-root` and `unit-type`.
 *
 * EAA-1838: `unitTypesById` is optional so the check degrades to "no objection"
 * for any caller with no vocabulary to hand, rather than forcing every call site
 * to plumb one through.
 */
export function classifyMoveCandidate(
    source: OrganizationTreeNode,
    candidate: Organization,
    roots: OrganizationTreeNode[],
    known: Map<string, Organization>,
    unitTypesById?: Map<string, OrgUnitType>
): MoveBlockReason | null {
    if (candidate.id === source.id) return 'self';
    if (candidate.id === source.organization.parent) return 'current-parent';
    if (!candidate.is_active) return 'inactive';
    if (checkSelfDescendant(roots, source.id, candidate.id)) return 'descendant';
    if (checkCrossRoot(source.organization, candidate.id, known)) return 'cross-root';
    if (
        unitTypesById &&
        checkUnitTypeContainment(source.organization, candidate, unitTypesById)
    ) {
        return 'unit-type';
    }
    return null;
}

/** Frame 31's grey right-hand label per blocked row. */
export const MOVE_BLOCK_LABELS: Record<MoveBlockReason, string> = {
    self: 'chính nó',
    'current-parent': 'cấp cha hiện tại',
    inactive: 'đang ngưng',
    descendant: 'nhánh con của nó',
    'cross-root': 'cây tổ chức khác',
    'unit-type': 'loại đơn vị không hợp',
};

/**
 * EAA-1818: portal-api returns the move-rule failures as ENGLISH strings
 * under `details.parent` — `OrganizationService.validate_parent_change` raises
 * `ValueError(<literal>)` and the view passes it straight through, with no
 * localization on the server side. This table is therefore the only place the
 * user-facing Vietnamese for a server-rejected move exists.
 *
 * Matching is by prefix on the exact literals rather than equality, so a
 * trailing-whitespace or punctuation tweak upstream degrades to the fallback
 * instead of leaking raw English. Any unrecognized message falls back to a
 * generic Vietnamese line plus the server text, so an unmapped future rule is
 * still actionable rather than silent.
 */
const PARENT_RULE_MESSAGES: Array<{ match: string; vi: string }> = [
    {
        match: 'This move would make the organization a descendant of itself',
        vi: 'Không thể chuyển một đơn vị vào chính nhánh con của nó.',
    },
    {
        match: 'Cannot move an organization to a different root tree',
        vi: 'Không thể chuyển đơn vị sang cây tổ chức khác — tạo đơn vị mới ở cây đó.',
    },
    {
        match: 'Cannot move an organization under an inactive parent',
        vi: 'Không thể chuyển vào đơn vị đang ngưng hoạt động — bật lại đơn vị đích trước.',
    },
    {
        match: 'Unknown parent organization',
        vi: 'Không tìm thấy đơn vị đích — có thể đơn vị vừa bị xoá hoặc bạn không còn quyền xem nó.',
    },
    // EAA-1838: rule 4 and the retype path, which shares this handler
    // (`OrganizationService.update` raises these and the view reports every
    // ValueError under `details.parent`, whichever key actually caused it).
    //
    // ORDER IS LOAD-BEARING HERE: matching is `startsWith` + first hit, and three
    // of these server literals begin with "Unit type". The generic containment
    // entry must therefore come LAST, or it would swallow the two specific ones
    // and show the wrong Vietnamese for a cleared/cross-tree type.
    {
        match: 'Unknown unit type',
        vi: 'Không tìm thấy loại đơn vị — có thể vừa bị đổi. Tải lại trang rồi thử lại.',
    },
    {
        match: 'Unit type cannot be cleared',
        vi: 'Đơn vị luôn phải có một loại — chọn loại khác thay vì bỏ trống.',
    },
    {
        match: 'Unit type belongs to a different organization tree',
        vi: 'Loại đơn vị này thuộc cây tổ chức khác — chọn loại trong tổ chức hiện tại.',
    },
    {
        match: 'A unit of type',
        vi: 'Loại đơn vị này không được đứng ở cấp gốc — chọn một cấp cha.',
    },
    // The server names both types ("Unit type 'X' cannot contain unit type 'Y'."),
    // but prefix matching cannot recover them, so this is the generic line;
    // `checkUnitTypeContainment` above supplies the named version on hover, where
    // both rows are already in hand.
    {
        match: 'Unit type',
        vi: 'Loại đơn vị không hợp — cấp cha không chứa được loại của đơn vị này.',
    },
];

export function translateParentRuleMessage(serverMessage: string | null): string | null {
    if (!serverMessage) return null;
    const hit = PARENT_RULE_MESSAGES.find((rule) => serverMessage.startsWith(rule.match));
    if (hit) return hit.vi;
    return `Đơn vị đích không hợp lệ: ${serverMessage}`;
}

export function describeTargetLabel(
    target: DragTarget,
    targetLabel: string,
    parentLabel: string
): string {
    if (target.mode === 'onto') {
        return `Thả vào ${targetLabel}`;
    }
    return `Thả vào ${parentLabel}`;
}
